/**
 * Locale-aware date formatting for the planner.
 *
 * Replaces six copy-pasted `MONTHS_FR` arrays that hardcoded French month names
 * into the weekly and meal-config screens, so those dates stayed French no
 * matter what the app language was.
 *
 * Callers pass the active language explicitly rather than letting these helpers
 * read it from i18n. That is deliberate: a component that passes
 * `i18n.language` from `useTranslation()` is subscribed to language changes and
 * re-renders when it flips. A helper that read the language internally would
 * format correctly on first paint and then go stale.
 */

/** Bare `YYYY-MM-DD` parses as UTC midnight, which shifts a day in negative-offset zones. Noon does not. */
const atNoon = (isoDate: string): Date => new Date(isoDate + 'T12:00:00');

export const localeFor = (language: string | undefined): string =>
  language === 'fr' ? 'fr-CA' : 'en-US';

const isFrench = (language: string | undefined): boolean => language === 'fr';

/**
 * A day and its month, ordered the way the language orders it:
 * `18 août` in French, `August 18` in English.
 */
export function formatDayMonth(
  isoDate: string,
  language: string | undefined,
  month: 'long' | 'short' = 'long'
): string {
  return atNoon(isoDate).toLocaleDateString(localeFor(language), {
    day: 'numeric',
    month,
  });
}

/**
 * The seven-day span starting at `weekStart`, collapsing the shared month when
 * the week does not straddle one:
 *
 *   fr  17 – 23 août        ·  28 août – 3 septembre
 *   en  August 17 – 23      ·  August 28 – September 3
 */
export function formatWeekRange(
  weekStart: string,
  language: string | undefined,
  month: 'long' | 'short' = 'long',
  until?: string
): string {
  const locale = localeFor(language);
  const start = atNoon(weekStart);
  const end = atNoon(until ?? weekStart);
  if (!until) end.setDate(end.getDate() + 6);

  const startMonth = start.toLocaleDateString(locale, { month });
  const endMonth = end.toLocaleDateString(locale, { month });
  const sameMonth = startMonth === endMonth;

  if (isFrench(language)) {
    return sameMonth
      ? `${start.getDate()} – ${end.getDate()} ${endMonth}`
      : `${start.getDate()} ${startMonth} – ${end.getDate()} ${endMonth}`;
  }

  return sameMonth
    ? `${startMonth} ${start.getDate()} – ${end.getDate()}`
    : `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}`;
}

/**
 * A weekday and its day of the month: `mercredi 30` in French,
 * `Wednesday 30` in English. `toLocaleDateString` with only these two fields
 * orders them `30 Wednesday` in en-US, so the two halves are joined by hand.
 */
export function formatWeekdayDay(isoDate: string, language: string | undefined): string {
  const d = atNoon(isoDate);
  const weekday = d.toLocaleDateString(localeFor(language), { weekday: 'long' });
  return `${weekday} ${d.getDate()}`;
}

/** The weekday alone: `mercredi` / `Wednesday`. */
export function formatWeekday(isoDate: string, language: string | undefined): string {
  return atNoon(isoDate).toLocaleDateString(localeFor(language), { weekday: 'long' });
}

/** Three-letter weekday, no period: `jeu` / `Thu`. For tight tiles. */
export function formatWeekdayShort(isoDate: string, language: string | undefined): string {
  return atNoon(isoDate)
    .toLocaleDateString(localeFor(language), { weekday: 'short' })
    .replace('.', '');
}
